const timestampOrNull = (value) => {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

const sequenceOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const sequence = Number(value);
  return Number.isFinite(sequence) ? sequence : null;
};

const selectedLifecycleAuthority = (value = {}) => {
  const patch = value?.patch && typeof value.patch === 'object' ? value.patch : value;
  const state = String(patch?.state ?? patch?.displayState ?? '').trim().toUpperCase();
  if (
    patch?.unavailable === true
    || patch?.bettable === false
    || patch?.isBettable === false
    || patch?.is_bettable === false
    || patch?.oddBettable === false
    || patch?.odd_bettable === false
    || patch?.marketBettable === false
    || patch?.market_bettable === false
    || patch?.locked === true
    || patch?.is_suspended === true
    || patch?.suspended === true
    || patch?.marketSuspended === true
    || patch?.market_suspended === true
    || ['LOCKED', 'SUSPENDED', 'UNAVAILABLE'].includes(state)
  ) return 'closed';
  if (
    patch?.bettable === true
    || patch?.isBettable === true
    || patch?.is_bettable === true
    || (['OPEN', 'AVAILABLE'].includes(state) && patch?.unavailable !== true)
  ) return 'open';
  return null;
};

const equalFreshnessLifecycleDecision = (incoming, existing) => {
  if (incoming?.lifecycleAuthority === 'closed' && existing?.lifecycleAuthority !== 'closed') {
    return false;
  }
  if (existing?.lifecycleAuthority === 'closed' && incoming?.lifecycleAuthority !== 'closed') {
    return true;
  }
  return null;
};

const selectedExactAuthorityDecision = (incoming, existing) => {
  const incomingExact = incoming?.authority === 'preview_rejection';
  const existingExact = existing?.authority === 'preview_rejection';
  if (incomingExact !== existingExact) return incomingExact ? false : true;
  return null;
};

const selectedExactRevisionDecision = (incoming, existing) => {
  if (
    incoming?.authority !== 'preview_rejection'
    || existing?.authority !== 'preview_rejection'
  ) return null;

  const incomingOrdinal = sequenceOrNull(incoming?.authorityOrdinal);
  const existingOrdinal = sequenceOrNull(existing?.authorityOrdinal);
  if (Number.isFinite(incomingOrdinal) && Number.isFinite(existingOrdinal)) {
    return existingOrdinal >= incomingOrdinal;
  }
  if (Number.isFinite(existingOrdinal)) return true;
  if (Number.isFinite(incomingOrdinal)) return false;

  const incomingObservedAt = timestampOrNull(incoming?.observedAt);
  const existingObservedAt = timestampOrNull(existing?.observedAt);
  if (Number.isFinite(incomingObservedAt) && Number.isFinite(existingObservedAt)) {
    if (incomingObservedAt === existingObservedAt) return true;
    return existingObservedAt > incomingObservedAt;
  }
  return null;
};

const selectedSequenceDecision = (incoming, existing) => {
  const incomingSequence = sequenceOrNull(incoming?.sequence);
  const existingSequence = sequenceOrNull(existing?.sequence);
  if (Number.isFinite(incomingSequence) && Number.isFinite(existingSequence)) {
    return existingSequence >= incomingSequence;
  }
  if (Number.isFinite(existingSequence)) return true;
  if (Number.isFinite(incomingSequence)) return false;
  return null;
};

const selectedFreshnessTimeRelation = (incoming, existing) => {
  const incomingSourceTime = incoming?.sourceReceivedAt;
  const existingSourceTime = existing?.sourceReceivedAt;
  const hasIncomingSource = Number.isFinite(incomingSourceTime);
  const hasExistingSource = Number.isFinite(existingSourceTime);
  if (hasIncomingSource && hasExistingSource) {
    if (incomingSourceTime !== existingSourceTime) {
      return incomingSourceTime > existingSourceTime ? 'incoming' : 'existing';
    }
    const incomingUpdatedAt = incoming?.updatedAt;
    const existingUpdatedAt = existing?.updatedAt;
    const hasIncomingUpdated = Number.isFinite(incomingUpdatedAt);
    const hasExistingUpdated = Number.isFinite(existingUpdatedAt);
    if (hasIncomingUpdated && hasExistingUpdated && incomingUpdatedAt !== existingUpdatedAt) {
      return incomingUpdatedAt > existingUpdatedAt ? 'incoming' : 'existing';
    }
    return 'equal';
  }
  const incomingUpdatedAt = incoming?.updatedAt;
  const existingUpdatedAt = existing?.updatedAt;
  const hasIncomingUpdated = Number.isFinite(incomingUpdatedAt);
  const hasExistingUpdated = Number.isFinite(existingUpdatedAt);
  if (hasIncomingUpdated && hasExistingUpdated) {
    if (incomingUpdatedAt === existingUpdatedAt) return 'equal';
    return incomingUpdatedAt > existingUpdatedAt ? 'incoming' : 'existing';
  }
  if (hasIncomingUpdated !== hasExistingUpdated) {
    const incomingHasAnyTime = hasIncomingSource || hasIncomingUpdated;
    const existingHasAnyTime = hasExistingSource || hasExistingUpdated;
    if (incomingHasAnyTime !== existingHasAnyTime) {
      return incomingHasAnyTime ? 'incoming' : 'existing';
    }
    return 'unknown';
  }
  const incomingHasAnyTime = hasIncomingSource || hasIncomingUpdated;
  const existingHasAnyTime = hasExistingSource || hasExistingUpdated;
  if (incomingHasAnyTime !== existingHasAnyTime) {
    return incomingHasAnyTime ? 'incoming' : 'existing';
  }
  return 'unknown';
};

export const selectedPatchFreshness = (patch = {}, sequence = null, observedAt = Date.now()) => ({
  sourceReceivedAt: timestampOrNull(patch.sourceReceivedAt ?? patch.source_received_at),
  updatedAt: timestampOrNull(patch.updatedAt ?? patch.updated_at),
  authority: patch.authority ?? null,
  authorityOrdinal: sequenceOrNull(patch.authorityOrdinal),
  lifecycleAuthority: selectedLifecycleAuthority(patch),
  sequence,
  observedAt,
});

export const shouldPreserveExistingSelectedUpdate = (incoming, existing) => {
  if (!existing) return false;
  const timeRelation = selectedFreshnessTimeRelation(incoming, existing);
  if (timeRelation === 'incoming') return false;
  if (timeRelation === 'existing') return true;
  if (timeRelation === 'equal') {
    const lifecycleDecision = equalFreshnessLifecycleDecision(incoming, existing);
    if (lifecycleDecision != null) return lifecycleDecision;
    const exactAuthorityDecision = selectedExactAuthorityDecision(incoming, existing);
    if (exactAuthorityDecision != null) return exactAuthorityDecision;
    const exactRevisionDecision = selectedExactRevisionDecision(incoming, existing);
    if (exactRevisionDecision != null) return exactRevisionDecision;
    return selectedSequenceDecision(incoming, existing) ?? true;
  }
  const lifecycleDecision = equalFreshnessLifecycleDecision(incoming, existing);
  if (lifecycleDecision != null) return lifecycleDecision;
  const exactAuthorityDecision = selectedExactAuthorityDecision(incoming, existing);
  if (exactAuthorityDecision != null) return exactAuthorityDecision;
  const exactRevisionDecision = selectedExactRevisionDecision(incoming, existing);
  if (exactRevisionDecision != null) return exactRevisionDecision;
  const sequenceDecision = selectedSequenceDecision(incoming, existing);
  if (sequenceDecision != null) return sequenceDecision;
  return false;
};

export const shouldPreserveNewerSelectedWs = (restUpdate, wsAtRequest, latestWs) => {
  if (!latestWs) return false;
  const restHasTimeEvidence = Number.isFinite(restUpdate?.sourceReceivedAt)
    || Number.isFinite(restUpdate?.updatedAt);
  if (!restHasTimeEvidence && latestWs === wsAtRequest) {
    if (latestWs.authority === 'preview_rejection') return true;
    return false;
  }
  const timeRelation = selectedFreshnessTimeRelation(restUpdate, latestWs);
  if (timeRelation === 'incoming') return false;
  if (timeRelation === 'existing') return true;
  if (timeRelation === 'equal') {
    const lifecycleDecision = equalFreshnessLifecycleDecision(restUpdate, latestWs);
    if (lifecycleDecision != null) return lifecycleDecision;
    const exactAuthorityDecision = selectedExactAuthorityDecision(restUpdate, latestWs);
    if (exactAuthorityDecision != null) return exactAuthorityDecision;
    return selectedExactRevisionDecision(restUpdate, latestWs) ?? true;
  }
  if (latestWs === wsAtRequest) return false;
  const lifecycleDecision = equalFreshnessLifecycleDecision(restUpdate, latestWs);
  if (lifecycleDecision != null) return lifecycleDecision;
  const exactAuthorityDecision = selectedExactAuthorityDecision(restUpdate, latestWs);
  if (exactAuthorityDecision != null) return exactAuthorityDecision;
  const exactRevisionDecision = selectedExactRevisionDecision(restUpdate, latestWs);
  if (exactRevisionDecision != null) return exactRevisionDecision;
  return latestWs !== wsAtRequest;
};
